import apis from "@/apis";

/**
 * Determines the correct route for a notification based on its title and description.
 * @param {Object} notification - The notification object from the API.
 * @returns {Promise<Object>} A Vue Router location object (e.g., { name: 'route-name' }).
 */
export const getNotificationRoute = async (notification) => {
  // Exit workflow notifications carry the "Exit Requests" menu (menu_url =
  // /exit-workflow/inbox), but should open the request itself:
  // /exit-workflow/<exit_request_id>, from the "EXT-<year>-<id>" request_no.
  if ((notification.notification_title || "").toLowerCase().includes("exit request")) {
    const match = (notification.notification_description || "").match(/EXT-\d{4}-(\d+)/i);
    if (match) return { path: `/exit-workflow/${parseInt(match[1], 10)}` };
  }

  try {
    const response = await apis.getEmployeeNotificationById({ notification_id: notification.notification_id });
    const notificationData = response.data?.data?.[0];
    
    if (notificationData?.menu_url && notificationData.menu_url.trim() !== "") {
      return { path: notificationData.menu_url };
    }
  } catch (error) {
    console.error("Error fetching notification details for route:", error);
  }
  // If the backend provided a specific menu_url for this notification, use it directly.
  if (notification.menu_url && notification.menu_url.trim() !== "") {
    return { path: notification.menu_url };
  }

  const title = (notification.notification_title || "").toLowerCase();
  const description = (notification.notification_description || "").toLowerCase();

  // Exit workflow (Master/models/ExitWorkflow.js _notifyStatusChange): the
  // description carries the request_no "EXT-<year>-<id>" — open that
  // request; the detail page re-checks the viewer's access server-side.
  if (title.includes("exit request")) {
    const match = (notification.notification_description || "").match(/EXT-\d{4}-(\d+)/i);
    if (match) {
      return { name: "exit-workflow-detail", params: { exit_request_id: String(parseInt(match[1], 10)) } };
    }
    return { name: "exit-workflow-inbox" };
  }

  // Profile / KYC related
  if (title.includes("kyc") || title.includes("profile") || description.includes("kyc") || description.includes("profile")) {
    return { name: "profileview", params: { employee_id: notification.employee_id || 'my' } };
  }

  // Sprint task assignment / status-change notifications -> Sprint Board.
  // Falls back here whenever the backend notification has no menu_url yet
  // (no matching row in the shared `menu` table), so the click still lands
  // somewhere useful instead of the generic message view.
  if (title.includes("task") || title.includes("sprint") || description.includes("task")) {
    return { name: "sf-board-all" };
  }

  // Default to generic notification details for everything else
  // This ensures the user sees the actual notification message first.
  return {
    name: "displayNotification",
    params: { notification_id: notification.notification_id },
  };
};

